<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4">
    <div class="w-full max-w-md">
      <!-- 提示卡片 -->
      <div class="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 transition-all duration-200 hover:shadow-sm">
        <div class="flex items-start gap-3">
          <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-semibold text-blue-800">安全提醒</h3>
            <p class="text-sm text-blue-700 mt-1">您当前使用的是默认密码，为保障账户安全，请先修改密码后再继续使用系统。</p>
          </div>
        </div>
      </div>

      <!-- 修改密码卡片 -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/50 p-8">
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-500/30">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <h2 class="text-xl font-bold text-slate-800">修改密码</h2>
          <p class="text-sm text-slate-500 mt-2">请设置新的登录密码</p>
        </div>

        <div class="space-y-5">
          <!-- 旧密码 -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">旧密码</label>
            <div class="relative">
              <input
                v-model="passwordForm.oldPassword"
                :type="showOldPassword ? 'text' : 'password'"
                class="form-input w-full !pr-12"
                placeholder="请输入旧密码"
                @input="clearPasswordError"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors duration-200"
                @click="showOldPassword = !showOldPassword"
              >
                <svg v-if="showOldPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- 新密码 -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">新密码</label>
            <div class="relative">
              <input
                v-model="passwordForm.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input w-full !pr-12"
                placeholder="6-20位，必须包含大小写字母和数字"
                @input="clearPasswordError"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors duration-200"
                @click="showNewPassword = !showNewPassword"
              >
                <svg v-if="showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </button>
            </div>
            <!-- 密码强度指示器 -->
            <div v-if="passwordForm.newPassword" class="mt-2">
              <div class="flex gap-1">
                <div
                  v-for="i in 4"
                  :key="i"
                  :class="[
                    'h-1 flex-1 rounded-full transition-all duration-300',
                    passwordStrength >= i ? strengthColor : 'bg-slate-200'
                  ]"
                />
              </div>
              <p class="text-xs mt-1" :class="strengthTextColor">{{ strengthLabel }}</p>
            </div>
          </div>

          <!-- 确认新密码 -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">确认新密码</label>
            <div class="relative">
              <input
                v-model="passwordForm.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :class="[
                  'form-input w-full !pr-16',
                  confirmPasswordError
                    ? '!border-red-500 focus:!ring-red-500'
                    : passwordForm.confirmPassword && !confirmPasswordError && passwordForm.newPassword === passwordForm.confirmPassword
                      ? '!border-green-500 focus:!ring-green-500'
                      : ''
                ]"
                placeholder="请再次输入新密码"
                @input="clearPasswordError"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors duration-200"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <svg v-if="showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </button>
              <!-- 匹配图标 -->
              <div v-if="passwordForm.confirmPassword && !confirmPasswordError && passwordForm.newPassword === passwordForm.confirmPassword" class="absolute right-10 top-1/2 -translate-y-1/2">
                <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- 错误提示 -->
        <p v-if="passwordError" class="mt-4 text-sm text-red-600 flex items-center gap-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          {{ passwordError }}
        </p>

        <!-- 操作按钮 -->
        <div class="mt-8 space-y-3">
          <button
            class="btn-primary w-full !py-3 !text-base font-medium"
            :disabled="submitting"
            @click="handleChangePassword"
          >
            <span v-if="submitting" class="flex items-center justify-center gap-2">
              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              提交中...
            </span>
            <span v-else>确认修改</span>
          </button>

          <button
            class="w-full py-3 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 cursor-pointer"
            @click="handleLogout"
          >
            退出登录
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { useRouterStore } from '@/stores/router'
import { changePassword } from '@/api/auth'
import router from '@/router'

const userStore = useUserStore()

const submitting = ref(false)
const passwordError = ref('')
const passwordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})
const showOldPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

function clearPasswordError() {
  passwordError.value = ''
}

const confirmPasswordError = computed(() => {
  return passwordForm.value.confirmPassword !== '' &&
    passwordForm.value.newPassword !== '' &&
    passwordForm.value.confirmPassword !== passwordForm.value.newPassword
})

// 密码强度计算
const passwordStrength = computed(() => {
  const password = passwordForm.value.newPassword
  if (!password) return 0
  let strength = 0
  if (password.length >= 6) strength++
  if (password.length >= 10) strength++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++
  if (/\d/.test(password)) strength++
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++
  return Math.min(strength, 4)
})

const strengthColor = computed(() => {
  const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500']
  return colors[passwordStrength.value - 1] || 'bg-slate-200'
})

const strengthLabel = computed(() => {
  const labels = ['风险', '低', '中', '高']
  return labels[passwordStrength.value - 1] || ''
})

const strengthTextColor = computed(() => {
  const colors = ['text-red-600', 'text-orange-600', 'text-yellow-600', 'text-green-600']
  return passwordStrength.value > 0 ? colors[passwordStrength.value - 1] : 'text-slate-500'
})

async function handleLogout() {
  userStore.clearUser()
  const routerStore = useRouterStore()
  routerStore.resetRoutes()
  router.push('/login')
}

async function handleChangePassword() {
  passwordError.value = ''

  if (!passwordForm.value.oldPassword) {
    passwordError.value = '请输入旧密码'
    return
  }
  if (!passwordForm.value.newPassword) {
    passwordError.value = '请输入新密码'
    return
  }
  const password = passwordForm.value.newPassword
  if (password.length < 6 || password.length > 20) {
    passwordError.value = '密码长度必须在6-20位之间'
    return
  }
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    passwordError.value = '密码必须包含大小写字母和数字'
    return
  }
  if (password !== passwordForm.value.confirmPassword) {
    passwordError.value = '两次输入的密码不一致'
    return
  }

  try {
    submitting.value = true
    await changePassword({
      oldPassword: passwordForm.value.oldPassword,
      newPassword: passwordForm.value.newPassword
    })

    ElMessage.success('密码修改成功，请重新登录')
    // 密码修改后 token 已失效，直接清除本地状态跳转登录页，无需调用 logout 接口
    userStore.clearUser()
    const routerStore = useRouterStore()
    routerStore.resetRoutes()
    router.push('/login')
  } catch (error) {
    passwordError.value = error.message || '修改失败，请检查旧密码是否正确'
  } finally {
    submitting.value = false
  }
}
</script>
